import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { userApi, apiError } from "@/lib/userApi";
import { toast } from "sonner";
import { NoirBg } from "@/components/NoirBg";
import { GlassField } from "@/components/GlassBits";
import { ProductCard } from "@/components/market/ProductCard";
import { ProductDetail } from "@/components/market/ProductDetail";
import { ProductForm } from "@/components/market/ProductForm";
import { Loader2, Store, Plus, Search, MessagesSquare, Newspaper, Wand2 } from "lucide-react";

const FILTERS = [
  { key: "", label: "Todo" },
  { key: "disponible", label: "Disponibles" },
  { key: "vendido", label: "Vendidos" },
  { key: "mine", label: "Mis productos" },
];

export default function Market() {
  const [products, setProducts] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("");
  const [open, setOpen] = useState(null);
  const [form, setForm] = useState(null);

  const load = useCallback(async () => {
    try {
      const params = { q };
      if (filter === "mine") params.mine = true;
      else if (filter) params.status = filter;
      const { data } = await userApi.get("/market/products", { params });
      setProducts(data.products);
    } catch (e) {
      toast.error(apiError(e, "No se pudo cargar el mercado"));
      setProducts([]);
    }
  }, [q, filter]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen noir-bg theme-noir" style={{ backgroundColor: "#050507" }}>
      <NoirBg />

      <header className="sticky top-0 z-30 border-b border-white/10 backdrop-blur-xl"
        style={{ background: "rgba(10,3,4,0.55)" }}>
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <img src="/logo-digital-forge.jpg" alt="Digital Forge" data-testid="brand-logo"
              className="w-9 h-9 rounded-md object-cover shrink-0 border border-[#FF8A2B]/40"
              style={{ boxShadow: "0 0 14px rgba(255,138,43,0.28)" }} />
            <div className="font-heading font-bold text-sm text-white">Mercado</div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/muro" data-testid="market-to-feed"
              className="glass-btn px-3 py-2 rounded-xl text-xs flex items-center gap-1.5">
              <Newspaper className="w-3.5 h-3.5" /> Muro
            </Link>
            <Link to="/comunidad" data-testid="market-to-community"
              className="glass-btn px-3 py-2 rounded-xl text-xs flex items-center gap-1.5">
              <MessagesSquare className="w-3.5 h-3.5" /> Chats
            </Link>
            <Link to="/panel" data-testid="market-to-editor"
              className="glass-btn px-3 py-2 rounded-xl text-xs flex items-center gap-1.5">
              <Wand2 className="w-3.5 h-3.5" /> Editor
            </Link>
            <button data-testid="new-product-btn" onClick={() => setForm({})}
              className="glass-btn glass-btn-red px-3 py-2 rounded-xl text-xs flex items-center gap-1.5">
              <Plus className="w-3.5 h-3.5" /> Vender
            </button>
          </div>
        </div>
      </header>

      <main className="relative z-10 max-w-6xl mx-auto px-4 py-6">
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-white/35 absolute left-3 top-1/2 -translate-y-1/2 z-10" />
            <GlassField value={q} data-testid="market-search" inputClass="pl-9"
              placeholder="Busca por nombre o descripción"
              onChange={(e) => setQ(e.target.value)} />
          </div>
          <div className="flex gap-2 overflow-x-auto">
            {FILTERS.map((f) => (
              <button key={f.key || "all"} data-testid={`market-filter-${f.key || "all"}`}
                onClick={() => setFilter(f.key)}
                className={`glass-btn ${filter === f.key ? "glass-btn-red" : ""} px-3.5 py-2.5 rounded-xl text-xs whitespace-nowrap`}>
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {products === null ? (
          <Loader2 className="w-7 h-7 animate-spin text-red-400 mx-auto mt-16" />
        ) : products.length === 0 ? (
          <div className="glass-card p-10 text-center max-w-md mx-auto" data-testid="market-empty">
            <Store className="w-8 h-8 text-red-400/70 mx-auto mb-3" />
            <div className="text-white font-semibold">Todavía no hay nada aquí</div>
            <p className="text-sm text-white/55 mt-2">
              Sé el primero: publica algo con fotos y precio, y los miembros te escribirán por chat.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="market-grid">
            {products.map((p, i) => (
              <div key={p.product_id} className="noir-rise"
                style={{ animationDelay: `${Math.min(i, 8) * 60}ms` }}>
                <ProductCard product={p} onOpen={setOpen} />
              </div>
            ))}
          </div>
        )}
      </main>

      {open && (
        <ProductDetail product={open} onClose={() => setOpen(null)}
          onEdit={(p) => { setOpen(null); setForm(p); }}
          onDeleted={() => { setOpen(null); load(); }}
          onChanged={() => { setOpen(null); load(); }} />
      )}

      {form && (
        <ProductForm product={form.product_id ? form : null} onClose={() => setForm(null)}
          onSaved={() => { setForm(null); load(); }} />
      )}
    </div>
  );
}
